/**
 * SideNavbarLayout — the logged-in app shell: top bar, side navigation and
 * content area, for staff and families alike.
 *
 * 2026-10-31 (PM efb0e798), Accelimate-style shell: a top bar (club logo and
 * name, the side-navigation collapse control, search with Ctrl+K, help,
 * theme, notifications, the account menu) over an icon rail of sections
 * whose pages list in a second column; on a phone the rail and page list
 * are a slide-in drawer. The pieces live in TopBar, TopBarSearch, SideNav,
 * nav/NavRail, nav/SectionNav, nav/AccountMenu and nav/railModel; this file
 * keeps the state, the role and permission filtering, the search index, the
 * banners and the profile dialog.
 *
 * The menu data lives in ./nav/navConfig (2026-06 nav refresh). The
 * desktop collapse state persists per-user via useUserPreferences.
 * Phone detection is plain media queries (useViewportFlags).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Input, Button } from "../ui";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { supabase } from "../../utils/supabaseClient";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsAdmin,
  selectIsMember,
  selectIsStaff,
  selectPermissions,
  setIsAdmin,
  setIsMember,
  setIsStaff,
} from "../../store/slices/authSlice";
import { selectSettings } from "../../store/slices/settingsSlice";
import { buildSearchIndex, kbArticlesToSearchItems } from "../../search/buildSearchIndex";
import type { SearchItem } from "../../search/searchTypes";
import type { ServerSearchType } from "../../api/searchAPI";
import { buildReportCategories, visibleReportCategories } from "../reports/reportsConfig";
import { settingsCategories } from "../settings/settingsConfig";
import { getAllKnowledgebaseArticles } from "../../api/knowledgebaseApi";
import CallStatusToggle from "../calls/CallStatusToggle";
import OverdueActionsModal from "../common/OverdueActionsModal";
import { useTheme } from "../../context/ThemeContext";
import { useLanguage } from "../../context/LanguageContext";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  changePassword,
  getUserDetail,
  updateEmailSelf,
  updateMyAvatar,
  updateMyName,
} from "../../api/usersAPI";
import { getAuthSource } from "../../api/apiHelpers";
import MediaLibrary from "../common/MediaLibrary";
import { useAppVersion } from "../../hooks/useAppVersion";
import { performLogout } from "../../utils/logout";
import {
  resolveStaffHomeRoute,
  staffHomeRoutePath,
} from "../../utils/staffHomeRoute";
import StaffPreferencesForm from "../common/StaffPreferencesForm";
import TrialBanner from "../trial/TrialBanner";
import { getTrialStatus, TrialStatus } from "../../api/trialAPI";
import StripeKillswitchBanner from "../common/StripeKillswitchBanner";
import PendingWaiversBanner from "../waivers/PendingWaiversBanner";
import { captureError } from "../../utils/sentry";
import { normalizeRole } from "../../utils/roles";
import { useEntitlements } from "../../hooks/useModuleEntitled";
import { useUserPreferences, usePersistedFilter } from "../../hooks/useUserPreferences";
import { useViewportFlags } from "../../hooks/useViewportFlags";
import {
  buildManageMenuGroups,
  buildMemberMenus,
  NavGroup,
  NavSectionConfig,
} from "./nav/navConfig";
import {
  buildMemberRail,
  buildStaffRail,
  findActive,
  isSinglePage,
  RailSection,
  sectionPages,
} from "./nav/railModel";
import AccountMenu from "./nav/AccountMenu";
import TopBar from "./TopBar";
import SideNav, { RAIL_AND_PANEL_WIDTH_CLASS, RAIL_WIDTH_CLASS } from "./SideNav";
import { getPrimaryText } from "../../utils/themeUtils";
import CCManagementDrawer from "./CCManagementDrawer";
import { isPlatformStaff } from "../../utils/platformStaff";
import MembershipReactivationPrompt from "../membership/MembershipReactivationPrompt";
import { useWhatsNew } from "../../hooks/useWhatsNew";
import WhatsNewModal from "../whatsnew/WhatsNewModal";
import { PASSWORD_RULE_TEXT, passwordProblem } from "../../utils/passwordRule";

// The top-bar search covers EVERY record, leads included (Dan 2026-09-30: staff
// could not find a lead, Ella Scheivert, from here).
const SIDEBAR_SEARCH_TYPES: ServerSearchType[] = ["members", "customers", "leads", "athletes"];

interface AdminLayoutProps {
  children: React.ReactNode;
}

const SideNavbarLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // phone drawer
  const [settings, setSettings] = useState<any>(null);
  const { isMobileDevice } = useViewportFlags();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  // Club Central platform tools, opened from the account menu (platform staff only)
  const [ccToolsOpen, setCcToolsOpen] = useState(false);
  const [formData, setFormData] = useState<any>({
    newPassword: "",
    confirmNewPassword: "",
    currentPassword: "",
    firstName: "",
    lastName: "",
    email: "",
    avatar_url: "",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [, setUser] = useState<any>();
  const [, setLoading] = useState(false);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [overdueActions, setOverdueActions] = useState<any[]>([]);
  // Trial status for members
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [showTrialBanner, setShowTrialBanner] = useState(true);
  // Access user roles from Redux store
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);
  const isMember = useSelector(selectIsMember);
  const permissions: any = useSelector(selectPermissions);
  const tenantSettings: any = useSelector(selectSettings);

  // "What's New" changelog — staff/admin only. Auto-opens once per session
  // when there's unseen content and onboarding isn't on screen.
  const whatsNew = useWhatsNew(isAdmin || isStaff);

  // ── Per-user persisted sidebar state ─────────────────────────────
  // Desktop collapse (page list hidden, rail only) survives reloads and
  // follows the user across devices (users.preferences jsonb).
  const userPrefs = useUserPreferences();
  const [isCollapsed, setIsCollapsed] = usePersistedFilter<boolean>(
    userPrefs,
    "nav_collapsed",
    false,
    (v) => typeof v === "boolean"
  );

  // Storefront is dormant by default. Only render the member-side store
  // link when the tenant has flipped storefront_visibility to a state
  // that surfaces the storefront. NULL/missing → 'disabled'.
  const storefrontVisible =
    tenantSettings?.storefront_visibility === "members_only" ||
    tenantSettings?.storefront_visibility === "public";

  // Facility booking is dormant on every tenant until the owner
  // (a) flips settings.facility_booking_enabled, AND
  // (b) publishes at least one resource with member-visible
  //     visibility (public or members_only). The
  //     /facility/feature-status endpoint returns both.
  // Probe on mount; hide the member nav links until both are true.
  const [facilityVisible, setFacilityVisible] = useState(false);
  useEffect(() => {
    if (!isMember) return;
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { getFeatureStatus } = await import("../../api/facilityAPI");
        const res = await getFeatureStatus(authToken);
        if (cancelled) return;
        if (res.success && res.data.enabled && res.data.bookable_count > 0) {
          setFacilityVisible(true);
        }
      } catch {
        /* network failure → fail closed (link stays hidden) */
      }
    })();
    return () => { cancelled = true; };
  }, [isMember]);

  // Participant terminology — falls back to 'Athlete' / 'Athletes'
  // for any tenant that hasn't customized it (= today's behavior).
  const participantSingular =
    (tenantSettings?.participant_label_singular || "").trim() || "Athlete";
  const participantPlural =
    (tenantSettings?.participant_label_plural || "").trim() || "Athletes";

  // userDetails lives in localStorage but we mirror it into React state so
  // an in-session update (e.g. StaffPreferencesForm saving a new default
  // home page) can refresh the navbar logo without a full reload.
  // `user-details-updated` is dispatched by anything that rewrites the
  // storage key; the storage event covers cross-tab changes.
  const readUserDetails = () => {
    try { return JSON.parse(localStorage.getItem("userDetails") || "{}"); }
    catch { return {}; }
  };
  const [userDetails, setUserDetails] = useState<any>(readUserDetails);
  useEffect(() => {
    const refresh = () => setUserDetails(readUserDetails());
    const onStorage = (e: StorageEvent) => {
      if (e.key === "userDetails") refresh();
    };
    window.addEventListener("user-details-updated", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("user-details-updated", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  const token = localStorage.getItem("authToken");
  const authSource = localStorage.getItem("authSource");
  const userRole = localStorage.getItem("userRole");

  // Resolve where the navbar logo should point for staff/admin/manager.
  // Per-user override → tenant default → 'dashboard'. Falls back to
  // settings.staff_default_home_route for users who logged in before this
  // field landed on /api/auth/current-user (stale userDetails in storage).
  const resolvedStaffLogoRoute = resolveStaffHomeRoute(
    userDetails?.staff_home_route,
    userDetails?.tenant_default_home_route ||
      (tenantSettings as any)?.staff_default_home_route,
  );
  const staffLogoUrl = staffHomeRoutePath(resolvedStaffLogoRoute, tenantId || "");

  // Platform admin check: central auth users (including those with tenant access) but NOT the actual tenant owner
  const isPlatformAdmin =
    (authSource === 'central' || authSource === 'tenant_owner') &&
    normalizeRole(userRole) !== 'tenant_owner';
  // CC Staff = Club Central platform team. Same condition as
  // isPlatformAdmin — the old `authSource === 'central'` check was a
  // latent bug: central login stamps source 'tenant_owner' for ANY
  // central user with tenant access, so ccStaffOnly items (Email
  // Verification) were invisible even to actual CC admins. See
  // utils/platformStaff.
  const isCCStaff = isPlatformAdmin;
  // Theme context
  const { isDarkMode } = useTheme();
  // Language context
  const { language, setLanguage, supportedLanguages, languageNames } = useLanguage();
  // Translation hook
  const { t } = useTranslation('common');
  const [, setError] = useState("");
  // App version
  const { currentVersion } = useAppVersion();
  // HELPER
  const dispatch = useDispatch();
  const storedSettings = localStorage.getItem("settings");

  // Admins bypass item-level permission checks — same rule
  // ProtectedRoute applies to the routes these items point at.
  const hasPermission = (perms: { name: string }[], required?: string) =>
    !required || isAdmin || perms?.some((perm) => perm.name === required);

  // Check if user has access to a navigation section
  // Admins always have access; staff need the section permission
  const hasSectionAccess = (sectionKey?: string) => {
    if (!sectionKey) return true; // No section restriction
    if (isAdmin) return true; // Admins see all sections
    return permissions?.some((p: { name: string }) => p.name === `section_${sectionKey}`);
  };

  // Module entitlement check. Hard-gated modules used to disappear from
  // the nav entirely; since the 2026-06 nav refresh they render LOCKED
  // (padlock, inert) so tenants can discover what an upgrade unlocks —
  // Avoca-style. Soft-gated items stay visible and clickable (the page
  // renders <ModuleGate>). Default-open while entitlements load so the
  // nav doesn't flicker.
  const { isModuleEntitled, loading: entitlementsLoading } = useEntitlements();
  const moduleGateState = (moduleKey?: string): "open" | "locked" => {
    if (!moduleKey) return "open";
    if (entitlementsLoading) return "open";
    const result = isModuleEntitled(moduleKey);
    if (result.granted) return "open";
    return result.gating === "hard" ? "locked" : "open";
  };

  // ── Menu construction (data lives in nav/navConfig) ──────────────
  const menuGroups = buildManageMenuGroups({
    tenantId,
    participantSingular,
    participantPlural,
    settings,
  });

  // Filter by section permission, adminOnly, and module entitlement.
  // Hard-gated modules become locked (visible + padlock) instead of
  // vanishing.
  const visibleGroups: NavGroup[] = menuGroups
    .map((group) => ({
      ...group,
      sections: group.sections
        .filter((section) => {
          if (!section.skipSectionPermission && !hasSectionAccess(section.sectionKey)) return false;
          if (section.adminOnly && !isAdmin) return false;
          // H3: hide the Attendance section entirely when the tenant set
          // attendance_mode='none' (online-only / no-attendance clubs).
          if (
            section.sectionKey === "attendance" &&
            settings?.attendance_mode === "none"
          ) {
            return false;
          }
          return true;
        })
        .map((section): NavSectionConfig => {
          const locked = moduleGateState(section.moduleKey) === "locked";
          const items = section.items
            .map((item: any) => {
              if (item.isCategory) return item;
              // A page moved into a merged section keeps its old section's gate.
              if (item.sectionPermission && !hasSectionAccess(item.sectionPermission)) return null;
              if (item.isNested) {
                if (item.adminOnly && !isAdmin) return null;
                const subItems = item.items.filter((sub: any) => {
                  if (sub.adminOnly && !isAdmin) return false;
                  if (sub.permission && !hasPermission(permissions, sub.permission)) return false;
                  return true;
                });
                return subItems.length > 0 ? { ...item, items: subItems } : null;
              }
              if (item.adminOnly && !isAdmin) return null;
              // CC-Staff-only items: hidden from everyone except central-auth
              // platform staff. Even tenant owners/admins shouldn't see them.
              if (item.ccStaffOnly && !isCCStaff) return null;
              if (item.permission && !hasPermission(permissions, item.permission)) return null;
              // Item-level hard gates stay hidden (per-item padlocks would
              // be noise); section-level gates are the discoverable ones.
              if (moduleGateState(item.moduleKey) === "locked") return null;
              return item;
            })
            .filter(Boolean)
            // A page-list heading with none of its pages left goes too, or a
            // merged section of hidden pages would still show.
            .filter(
              (item: any, i: number, kept: any[]) =>
                !item.isCategory || (i + 1 < kept.length && !kept[i + 1].isCategory)
            );
          return { ...section, items, locked };
        })
        // Remove sections with no visible items (keep direct links + locked)
        .filter((section) => section.isDirectLink || section.locked || section.items.length > 0),
    }))
    .filter((group) => group.sections.length > 0);

  // Global-search index: pages come from the SAME filtered nav the user sees
  // (so search can't surface inaccessible pages) + the reports/settings
  // catalogs. People are searched server-side inside SearchBar.
  // KB/Help articles for search — fetched once for the (persistent) staff
  // shell and folded into the search index as the "Help" group. Audience
  // filtering happens server-side; failure is non-fatal (no Help group).
  const [kbItems, setKbItems] = useState<SearchItem[]>([]);

  const searchIndex = useMemo(
    () => [
      ...buildSearchIndex({
        navGroups: visibleGroups,
        // Only reports this person can open, as on the Reports page.
        reportCategories: visibleReportCategories(buildReportCategories(participantSingular), {
          isAdmin: !!isAdmin,
          permissions,
          isCCStaff,
        }),
        settingsCategories,
        tenantId: tenantId || "",
        isCCStaff,
      }),
      ...kbItems,
    ],
    [visibleGroups, participantSingular, tenantId, isCCStaff, kbItems, isAdmin, permissions]
  );

  useEffect(() => {
    if (isMember || !tenantId) return;
    const token = localStorage.getItem("authToken");
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getAllKnowledgebaseArticles(token);
        if (cancelled) return;
        const articles =
          res?.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        setKbItems(kbArticlesToSearchItems(articles, tenantId));
      } catch {
        /* KB unavailable → no Help group */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMember, tenantId]);


  // Member menus
  const memberMenus = buildMemberMenus({ tenantId, t, facilityVisible, storefrontVisible });

  // ── Two-level navigation (PM efb0e798) ────────────────────────────
  // Rail sections come from the SAME filtered groups (staff) and member
  // menus (families) the sidebar listed, so nothing new becomes reachable.
  const railSections: RailSection[] = [
    ...(isAdmin || isStaff ? buildStaffRail(visibleGroups) : []),
    ...(isMember ? buildMemberRail({ tenantId, t, ...memberMenus }) : []),
  ];
  const { sectionKey: activeKey, href: activeHref } = findActive(railSections, location.pathname);
  // A section chosen on the rail shows its pages until the next navigation,
  // when the page list follows the route again. DISMISSED: the person
  // closed the list by choosing the shown section again.
  const DISMISSED = "__dismissed__";
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  useEffect(() => {
    setPickedKey(null);
  }, [location.pathname]);
  const railCollapsed = isCollapsed && !isMobileDevice;
  const shownKey =
    pickedKey === DISMISSED ? null : pickedKey ?? (railCollapsed ? null : activeKey);
  const shownSection =
    railSections.find((s) => s.key === shownKey && !s.locked && !isSinglePage(s)) ?? null;
  const pickSection = (key: string) =>
    setPickedKey(shownSection?.key === key ? DISMISSED : key);
  const panelDocked = !isMobileDevice && !isCollapsed && !!shownSection;

  // Help in the top bar. Staff: the Knowledgebase (it links to the Support
  // Center), else the Support Center, under the same gates the Support rail
  // section had (section_support + the page permission) before it left the
  // rail on 2026-10-31. Families: their Help page, if the rail has it.
  const reachable = railSections.flatMap((s) =>
    s.locked ? [] : [s.href, ...sectionPages(s).map((p) => p.to)]
  );
  const staffHelpHref =
    isMember || !hasSectionAccess("support")
      ? null
      : hasPermission(permissions, "page_knowledgebase")
      ? `/${tenantId}/manage/knowledgebase`
      : hasPermission(permissions, "page_support")
      ? `/${tenantId}/manage/support`
      : null;
  const memberHelpHref = `/${tenantId}/member/help`;
  const helpHref = staffHelpHref ?? (reachable.includes(memberHelpHref) ? memberHelpHref : null);

  const navigate = useNavigate();

  // Handle Logout action
  const handleLogout = async () => {
    // Use centralized logout utility - clears all user data, preserves tenant info
    const tenantSlug = await performLogout();

    // Reset Redux state
    dispatch(setIsAdmin(false));
    dispatch(setIsStaff(false));
    dispatch(setIsMember(false));

    // Redirect to login (use preserved tenant slug or current tenantId)
    navigate(`/${tenantSlug || tenantId}/login`);
  };

  // Close sidebar on window resize if desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (storedSettings) {
      setSettings(JSON.parse(storedSettings));
    }
  }, [storedSettings]);

  // Fetch trial status for members
  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (isMember && token) {
        const response = await getTrialStatus(token);
        if (response.success && response.data.is_trial) {
          setTrialStatus(response.data);
        }
      }
    };
    fetchTrialStatus();
  }, [isMember, token]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (
      !formData?.currentPassword ||
      !formData?.newPassword ||
      !formData?.confirmNewPassword
    ) {
      toast.error("Please fill in all fields.");
      return false;
    }
    if (formData?.newPassword !== formData?.confirmNewPassword) {
      toast.error("New password and confirmation do not match.");
      return false;
    }
    // The one password rule (utils/passwordRule), the same the server uses.
    const problem = passwordProblem(formData?.newPassword);
    if (problem) {
      toast.error(`${problem}.`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if password fields are filled, validate only if yes
    const shouldUpdatePassword =
      (formData?.currentPassword || "").trim() !== "" ||
      (formData?.newPassword || "").trim() !== "" ||
      (formData?.confirmNewPassword || "").trim() !== "";

    if (shouldUpdatePassword && !validateForm()) return;

    // Saving with nothing changed used to close the dialog without a word,
    // which reads as "saved" (2026-10-10). Say so and leave it open.
    const nameWillChange =
      (formData?.firstName ?? "") !== (userDetails?.first_name ?? "") ||
      (formData?.lastName ?? "") !== (userDetails?.last_name ?? "");
    const typedEmailNow = (formData?.email || "").trim();
    const emailWillChange = !!typedEmailNow && typedEmailNow !== (userDetails?.email || "");
    if (!nameWillChange && !shouldUpdatePassword && !emailWillChange) {
      toast.info("No changes to save.");
      return;
    }

    try {
      // Each part is saved and reported on its own. They used to run in a
      // chain behind the name update, which the server allows only for people
      // who may manage users: for everyone else it was refused, the catch
      // below said nothing, and the password and email were never attempted.
      // The password and email calls also resolve (never throw) on a refusal,
      // and their answers were discarded.
      const nameChanged =
        (formData?.firstName ?? "") !== (userDetails?.first_name ?? "") ||
        (formData?.lastName ?? "") !== (userDetails?.last_name ?? "");

      // Anything that did not save keeps the dialog open, so the person can
      // see what was refused and try again. Everything saved closes it.
      let allSaved = true;

      // The name is saved through the person's OWN profile endpoint. It used
      // to go through PUT /users/:id (for managing other staff), which finds
      // no row for a tenant owner: every save was a 404 and nothing was said
      // (2026-10-09 visual walk).
      if (nameChanged) {
        const nameResult = await updateMyName(token, {
          firstName: formData?.firstName,
          lastName: formData?.lastName,
        });
        if (nameResult.ok) {
          toast.success("Profile saved.");
        } else {
          allSaved = false;
          toast.error(nameResult.message || "Your name was not saved.");
        }
      }

      // Optionally update password. The server asks for the CURRENT password
      // (as `password`) from anyone who may not manage users, and checks it
      // against the address they sign in with today, not one being typed.
      if (shouldUpdatePassword) {
        const passwordResult = await changePassword({
          email: userDetails?.email || formData?.email,
          password: formData.currentPassword,
          newPassword: formData.newPassword,
          isAdmin: true, // ignored by the server; it decides from the caller
          userId: userDetails?.id,
        });
        if (passwordResult?.status === "success") {
          toast.success("Password changed.");
        } else {
          allSaved = false;
          toast.error(
            passwordResult?.message ||
              passwordResult?.error ||
              "Your password was not changed."
          );
        }
      }

      // Only a real, changed address is sent: a blank box (the address did
      // not load) is not a request to remove it.
      const typedEmail = (formData?.email || "").trim();
      if (typedEmail && typedEmail !== (userDetails?.email || "")) {
        // Resolves to the body on success and to the axios response (or
        // nothing, offline) on a refusal.
        const emailResult = await updateEmailSelf(token, {
          newEmail: formData.email,
        });
        if (emailResult?.success) {
          toast.success(emailResult?.message || "Email address updated.");
        } else {
          allSaved = false;
          toast.error(
            emailResult?.data?.error ||
              emailResult?.data?.message ||
              "Your email address was not changed."
          );
        }
      }
      fetchUser().catch((err) => {
        // fetchUser is a bare async function with no try/catch of its own —
        // an unguarded rejection here is unhandled and the user panel just
        // renders stale/empty.
        captureError(err, { component: "SideNavbarLayout", operation: "fetchUser" }, "warning");
      });
      if (allSaved) setIsProfileModalOpen(false);
    } catch (err) {
      setError("Failed to update staff details");
      toast.error("Your profile could not be saved. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateUserAvatar = async (publicUrl: any) => {
    // Server-side self-service write (PATCH /users/me/avatar) — only the
    // caller's own row; replaces the old direct Supabase users.update.
    // updateMyAvatar resolves null on failure (never rejects), so a falsy
    // check covers it. Was `=== undefined`, which only worked because the API
    // swallowed and returned undefined implicitly.
    const saved = await updateMyAvatar(token, publicUrl);
    if (!saved) {
      captureError(new Error("Avatar update failed"), { component: "SideNavbarLayout", operation: "updateAvatar" });
      toast.error("Couldn't update your photo.");
    }
  };

  const fetchUser = async () => {
    // Pass the auth source so central-auth users (tenant owners / platform
    // staff, who have NO tenant `users` row) are looked up via /users/central
    // instead of /users/:id — the latter 404s for them (Sentry CLUB-CENTRAL-
    // TENANT-2S, fetchUser at SideNavbarLayout). 2026-06-10.
    const res = await getUserDetail(token, userDetails?.id, getAuthSource());
    if (res?.status === 200) {
      // A central (tenant owner) profile may come back without an email; keep
      // the one already known rather than blanking the form and the stored copy.
      const fetched = res?.data?.user;
      const userData = fetched
        ? { ...fetched, email: fetched.email || readUserDetails()?.email || fetched.email }
        : fetched;
      setUser(userData);
      localStorage.setItem("userDetails", JSON.stringify(userData));
      // Notify any other live consumer (this same component's userDetails
      // mirror, future widgets, etc.) that the storage key changed.
      window.dispatchEvent(new Event("user-details-updated"));
      setFormData({
        firstName: userData?.first_name,
        lastName: userData?.last_name,
        email: userData?.email || "",
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
        avatar_url: userData?.avatar_url,
      });
    }
  };
  useEffect(() => {
    fetchUser().catch((err) => {
      // fetchUser is a bare async function with no try/catch of its own —
      // an unguarded rejection here is unhandled and the user panel just
      // renders stale/empty.
      captureError(err, { component: "SideNavbarLayout", operation: "fetchUser" }, "warning");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for overdue actions modal on mount
  useEffect(() => {
    if ((isAdmin || isStaff) && localStorage.getItem("showOverdueActionsModal") === "true") {
      const savedActions = localStorage.getItem("overdueActions");
      if (savedActions) {
        try {
          const actions = JSON.parse(savedActions);
          if (actions.length > 0) {
            setOverdueActions(actions);
            setShowOverdueModal(true);
          }
        } catch (error) {
          captureError(error, { component: "SideNavbarLayout", operation: "parseOverdueActions" }, "warning");
        }
      }
      // Clear the flag so it doesn't show again on page refresh
      localStorage.removeItem("showOverdueActionsModal");
    }
  }, [isAdmin, isStaff]);

  const handleCloseOverdueModal = () => {
    setShowOverdueModal(false);
    localStorage.removeItem("overdueActions");
  };

  const roleLabel = isAdmin ? "Admin" : isStaff ? "Staff" : "Member";
  const displayName =
    `${userDetails?.first_name || ""} ${userDetails?.last_name || ""}`.trim();

  const memberLanguagePicker = isMember ? (
    <div className="flex items-center w-full gap-2">
      <span className={`text-sm ${getPrimaryText(isDarkMode)}`}>{languageNames[language]}</span>
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value as 'en' | 'es')}
        className={`px-2 py-1 rounded-md border cursor-pointer text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cc-focus)] ${
          isDarkMode ? "bg-gray-800 text-gray-100 border-gray-600" : "bg-white text-gray-800 border-gray-300"
        }`}
      >
        {supportedLanguages.map((lang) => (
          <option key={lang} value={lang}>
            {languageNames[lang]}
          </option>
        ))}
      </select>
    </div>
  ) : undefined;

  const homeHref = isMember
    ? `/${tenantId}/dashboard`
    : isAdmin || isStaff
    ? staffLogoUrl
    : `/${tenantId}/`;

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}>
      <TopBar
        homeHref={homeHref}
        logoUrl={settings?.logo_url}
        businessName={settings?.business_name || "Club"}
        isMobileDevice={isMobileDevice}
        collapsed={isCollapsed}
        onToggleCollapse={() => {
          setPickedKey(null);
          setIsCollapsed(!isCollapsed);
        }}
        onOpenDrawer={() => setIsSidebarOpen(true)}
        search={!isMember ? { pages: searchIndex, serverTypes: SIDEBAR_SEARCH_TYPES } : null}
        helpHref={helpHref}
        isDarkMode={isDarkMode}
        callStatus={
          // Available / Busy / Off for routed calls. Renders nothing unless
          // this person answers calls at a club with routing.
          <CallStatusToggle />
        }
        accountMenu={
          <AccountMenu
            name={displayName}
            roleLabel={roleLabel}
            avatarUrl={formData?.avatar_url || userDetails?.avatar_url}
            isDarkMode={isDarkMode}
            onOpenProfile={() => setIsProfileModalOpen(true)}
            onLogout={handleLogout}
            isPlatformAdmin={isPlatformAdmin}
            onOpenPlatformTools={isPlatformStaff() ? () => setCcToolsOpen(true) : undefined}
            languagePicker={memberLanguagePicker}
            onOpenWhatsNew={(isAdmin || isStaff) ? whatsNew.openModal : undefined}
            whatsNewUnseen={whatsNew.unseen}
            versionLabel={currentVersion?.gitHash}
            versionTitle={
              currentVersion
                ? `Build: ${currentVersion.buildId} | Git: ${currentVersion.gitHash}`
                : undefined
            }
          />
        }
      />

      {/* Renders nothing for anyone but Club Central platform staff. */}
      <CCManagementDrawer open={ccToolsOpen} onClose={() => setCcToolsOpen(false)} />

      <SideNav
        sections={railSections}
        activeKey={activeKey}
        activeHref={activeHref}
        shownSection={shownSection}
        collapsed={isCollapsed}
        isMobileDevice={isMobileDevice}
        drawerOpen={isSidebarOpen}
        onCloseDrawer={() => setIsSidebarOpen(false)}
        onPick={pickSection}
        onDismiss={() => setPickedKey(DISMISSED)}
        isDarkMode={isDarkMode}
        versionLabel={currentVersion?.gitHash}
        versionTitle={
          currentVersion
            ? `Build: ${currentVersion.buildId} | Git: ${currentVersion.gitHash}`
            : undefined
        }
        drawerExtras={isMobileDevice ? <CallStatusToggle /> : undefined}
      />

      {/* Content Area: under the top bar, beside the side navigation. */}
      <div
        className={`min-w-0 pt-14 overflow-x-clip transition-all duration-300 ${
          isMobileDevice ? "" : panelDocked ? RAIL_AND_PANEL_WIDTH_CLASS : RAIL_WIDTH_CLASS
        } ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        {/* Trial banner for members on trial */}
        {isMember && trialStatus?.is_trial && showTrialBanner && tenantId && (
          <TrialBanner
            trialExpiresAt={trialStatus.trial_expires_at}
            tenantSlug={tenantId}
            membershipStatus={trialStatus.status}
            onClose={() => setShowTrialBanner(false)}
          />
        )}
        {/* Killswitch banner (shows only when engaged). Staff and admins only:
            it links to an admin setting, and a family seeing "Stripe is
            disabled" in red over every page has nothing it can do about it
            (PM f744a768). The server refuses card payments while it is on. */}
        {(isAdmin || isStaff) && <StripeKillswitchBanner />}
        {/* A reminder, never a wall: members with a waiver to sign. */}
        <PendingWaiversBanner enabled={!!isMember} />
        {children}
      </div>

      {/* Profile Modal */}
      {isProfileModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className={`relative w-full max-w-xl mx-4 p-6 rounded-lg shadow-xl ${
              isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            }`}
          >
            {/* Close Button */}
            <Button
              type="button"
              variant="neutral"
              size="sm"
              iconOnly
              onClick={() => setIsProfileModalOpen(false)}
              aria-label="Close modal"
              className="absolute top-4 right-4"
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Modal Title */}
            <h2 className="text-2xl font-bold mb-6">Profile Settings</h2>

            {/* Profile Form */}
            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Profile Image */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Profile Image
                </label>
                <div className="flex items-center gap-4">
                  <div
                    className={`w-20 h-20 rounded-full overflow-hidden flex items-center justify-center ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                    }`}
                  >
                    {formData.avatar_url ? (
                      <img
                        src={formData?.avatar_url}
                        alt="Avatar"
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <svg
                        className="w-12 h-12 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (setIsOpen) {
                        setIsOpen(true);
                      }
                    }}
                    className={`px-3 py-2 rounded-md text-sm ${
                      isDarkMode
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-gray-200 hover:bg-gray-300"
                    }`}
                  >
                    Choose from Library
                  </button>
                </div>
              </div>

              {/* First Name */}
              <div>
                <Input
                  label="First Name"
                  type="text"
                  name="firstName"
                  value={formData?.firstName}
                  placeholder="Enter first name"
                  onChange={handleChange}
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-800"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              {/* Last Name */}
              <div>
                <Input
                  label="Last Name"
                  type="text"
                  name="lastName"
                  value={formData?.lastName}
                  placeholder="Enter last name"
                  onChange={handleChange}
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-800"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              {/* Email Address */}
              <div>
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-800"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              {/* Password */}
              <div>
                <Input
                  label="Current Password"
                  type="password"
                  name="currentPassword"
                  value={formData?.currentPassword}
                  onChange={handleChange}
                  placeholder="Enter your current password"
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-800"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>
              <div className="flex gap-4 justify-between w-full">
                <div className="w-1/2">
                  <Input
                    label="New Password"
                    type="password"
                    name="newPassword"
                    value={formData?.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    hint={PASSWORD_RULE_TEXT}
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-800"
                        : "bg-white border-gray-300"
                    }`}
                  />
                </div>
                <div className="w-1/2">
                  <Input
                    label="Confirm New Password"
                    type="password"
                    name="confirmNewPassword"
                    value={formData?.confirmNewPassword}
                    onChange={handleChange}
                    placeholder="Enter the new password again"
                    className={`${
                      isDarkMode
                        ? "bg-gray-700 border-gray-800"
                        : "bg-white border-gray-300"
                    }`}
                  />
                </div>
              </div>

              {/* Staff/admin/manager preferences — default home + tips toggle.
                  Members don't see this; their home is always /tenant/dashboard. */}
              {(isAdmin || isStaff) && (
                <StaffPreferencesForm isDarkMode={isDarkMode} />
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsProfileModalOpen(false)}
                  className={`flex-1 px-4 py-2 rounded-md ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-800"
                      : "bg-gray-200 hover:bg-gray-300"
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 rounded-md transition-colors bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <MediaLibrary
        isOpen={isOpen ?? false}
        onClose={() => {
          if (setIsOpen) {
            setIsOpen(false);
          }
        }}
        onSelect={async (path) => {
          let publicUrl = path;

          // if path is just like "user_xxx/personal/image.png", generate full URL
          if (!path.startsWith("https://")) {
            const { data } = supabase.storage.from("media").getPublicUrl(path);
            publicUrl = data.publicUrl;
          }

          // Update profile data
          await updateUserAvatar(publicUrl);

          // Update local state (so image updates instantly)
          setFormData((prev: any) => ({ ...prev, avatar_url: publicUrl }));

          if (setIsOpen) {
            setIsOpen(false);
          }
        }}
        parentName={`personal/user_${userDetails?.id}`}
      />

      {/* Overdue Actions Modal */}
      <OverdueActionsModal
        isOpen={showOverdueModal}
        onClose={handleCloseOverdueModal}
        overdueActions={overdueActions}
      />

      {/* Any enrollment onto an INACTIVE membership offers to reactivate it —
          otherwise the athlete trains and nothing bills. Staff only (gated
          on manage_memberships inside). */}
      <MembershipReactivationPrompt />

      {whatsNew.open && (
        <WhatsNewModal
          entries={whatsNew.entries}
          total={whatsNew.total}
          isDarkMode={isDarkMode}
          onClose={whatsNew.close}
          onViewFull={() => {
            whatsNew.close();
            navigate(`/${tenantId}/manage/whats-new`);
          }}
        />
      )}
    </div>
  );
};

export default SideNavbarLayout;
